<template>
    <v-card flat class="comp-misure">
        <!-- Header sticky coerente -->
        <v-card-title class="comp-header pa-4 pb-2">
            <v-icon size="18" class="text-medium-emphasis me-2">mdi-ruler-square</v-icon>
            <span class="text-h6 font-weight-bold text-truncate">Misure</span>
            <v-spacer />
            <v-btn color="primary" size="small" @click="nuovaRiga" prepend-icon="mdi-plus">
                Nuova riga
            </v-btn>
        </v-card-title>

        <v-card-text class="pa-3 pt-2">
            <!-- ─── Stato vuoto ─── -->
            <div v-if="righeInterne.length === 0 && !loading" class="empty-state">
                <v-icon size="36" class="text-grey-lighten-1 mb-2">mdi-ruler-square</v-icon>
                <p class="text-body-2 text-medium-emphasis ma-0">Nessuna misura inserita</p>
                <v-btn size="small" variant="tonal" color="primary" class="mt-3" prepend-icon="mdi-plus"
                    @click="nuovaRiga">
                    Aggiungi la prima
                </v-btn>
            </div>

            <!-- ─── Tabella ─── -->
            <div v-else class="table-wrap">
                <v-data-table :headers="headers" :items="righeInterne" :loading="loading" density="compact"
                    hide-default-footer class="misure-table" :items-per-page="-1">
                    <!-- Colonna RIF: evidenziata -->
                    <template v-slot:[`item.rif`]="{ item }">
                        <span class="text-body-2 font-weight-semibold text-primary">{{ item.rif }}</span>
                    </template>

                    <!-- Colonna azioni: icone compatte -->
                    <template v-slot:[`item.actions`]="{ item }">
                        <div class="row-actions">
                            <v-btn icon size="x-small" variant="text" color="grey-darken-1" @click="modificaRiga(item)"
                                aria-label="Modifica">
                                <v-icon size="16">mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn icon size="x-small" variant="text" color="grey-darken-1" @click="duplicaRiga(item)"
                                aria-label="Duplica">
                                <v-icon size="16">mdi-content-copy</v-icon>
                            </v-btn>
                            <v-btn icon size="x-small" variant="text" color="error" @click="eliminaRiga(item)"
                                aria-label="Elimina">
                                <v-icon size="16">mdi-delete</v-icon>
                            </v-btn>
                        </div>
                    </template>
                </v-data-table>
            </div>
        </v-card-text>

        <!-- ─── Dialog: crea / modifica riga ─── -->
        <v-dialog v-model="dialogModifica" max-width="720" persistent>
            <v-card flat>
                <!-- Header dialog -->
                <v-card-title class="dialog-header pb-2">
                    <v-row class="ma-0 pa-0">
                        <span class="text-base font-weight-semibold">
                            {{ rigaCorrente.id ? 'Modifica riga' : 'Nuova riga' }}
                        </span>
                        <v-spacer />
                        <v-btn icon size="small" variant="text" @click="chiudiDialog">
                            <v-icon size="18">mdi-close</v-icon>
                        </v-btn>
                    </v-row>
                </v-card-title>

                <v-card-text class="pa-3 pt-2">
                    <v-form ref="formRigaRef">
                        <!-- Fila 1: riferimento + dimensioni principali -->
                        <v-row dense>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="rigaCorrente.rif" label="RIF." :rules="[rules.required]"
                                    hide-details="auto" density="compact" variant="outlined">
                                    <template #prepend-inner>
                                        <v-icon size="15" class="text-medium-emphasis">mdi-hashtag</v-icon>
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="rigaCorrente.tipo" label="Tipo" hide-details density="compact"
                                    variant="outlined" />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="3">
                                <v-text-field v-model="rigaCorrente.qta" label="Q.tà" type="number" hide-details
                                    density="compact" variant="outlined">
                                    <template #prepend-inner>
                                        <v-icon size="15" class="text-medium-emphasis">mdi-counter</v-icon>
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-text-field v-model="rigaCorrente.l" label="Larghezza" type="number" hide-details
                                    density="compact" variant="outlined">
                                    <template #prepend-inner>
                                        <v-icon size="15" class="text-medium-emphasis">mdi-resize</v-icon>
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-text-field v-model="rigaCorrente.h" label="Altezza" type="number" hide-details
                                    density="compact" variant="outlined">
                                    <template #prepend-inner>
                                        <v-icon size="15" class="text-medium-emphasis">mdi-resize</v-icon>
                                    </template>
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <!-- Fila 2: dettagli strutturali -->
                        <v-row dense>
                            <v-col cols="12" sm="3">
                                <v-text-field v-model="rigaCorrente.nBat" label="N. Bat" hide-details density="compact"
                                    variant="outlined" type="number" />
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-text-field v-model="rigaCorrente.spinta" label="Spinta" hide-details
                                    density="compact" variant="outlined" />
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-text-field v-model="rigaCorrente.maz" label="MAZ" hide-details density="compact"
                                    variant="outlined" />
                            </v-col>
                            <v-col cols="12" sm="3">
                                <v-text-field v-model="rigaCorrente.ar" label="AR" hide-details density="compact"
                                    variant="outlined" />
                            </v-col>
                        </v-row>

                        <!-- Fila 3: serratura, zanzariera, vetro -->
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-text-field v-model="rigaCorrente.serratura" label="Serratura" hide-details
                                    density="compact" variant="outlined" />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field v-model="rigaCorrente.zanz" label="Zanzariera" hide-details
                                    density="compact" variant="outlined" />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field v-model="rigaCorrente.vetro" label="Vetro" hide-details density="compact"
                                    variant="outlined" />
                            </v-col>
                        </v-row>

                        <!-- Fila 4: antone / persiana / scuro / batt -->
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-text-field v-model="rigaCorrente.antonePersiana" label="Antone / Persiana"
                                    hide-details density="compact" variant="outlined" />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field v-model="rigaCorrente.scuroInterno" label="Scuro interno" hide-details
                                    density="compact" variant="outlined" />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field v-model="rigaCorrente.batt" label="Batt" hide-details density="compact"
                                    variant="outlined" />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="12">
                                <v-textarea v-model="rigaCorrente.note" label="Note" hide-details density="compact"
                                    variant="outlined" :rows="3" autoGrow :max-rows="6" />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <!-- Actions -->
                <v-card-actions class="pt-0">
                    <v-spacer />
                    <v-btn size="small" variant="text" @click="chiudiDialog">Annulla</v-btn>
                    <v-btn size="small" color="primary" :loading="loading" @click="salvaRiga"
                        prepend-icon="mdi-content-save">
                        Salva
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { ref, watch } from 'vue'
import { crea_riga_misura, modifica_riga_misura, elimina_riga_misura } from '@/api/api.misure.js'

export default {
    name: 'TabellaMisure',

    props: {
        ordineId: {
            type: [String, Number],
            required: true
        },
        righe: {
            type: Array,
            default: () => []
        }
    },

    emits: ['righe-aggiornate'],

    setup(props, { emit }) {
        const loading = ref(false)
        const dialogModifica = ref(false)
        const formRigaRef = ref(null)
        const righeInterne = ref([...props.righe])

        const headers = [
            { title: 'RIF.', value: 'rif', sortable: true, width: '70' },
            { title: 'Tipo', value: 'tipo', sortable: false, width: '80' },
            { title: 'Q.tà', value: 'qta', sortable: false, width: '55', align: 'end' },
            { title: 'L', value: 'l', sortable: false, width: '70' },
            { title: 'H', value: 'h', sortable: false, width: '70' },
            { title: 'N. Bat', value: 'nBat', sortable: false, width: '70' },
            { title: 'Spinta', value: 'spinta', sortable: false, width: '70' },
            { title: 'MAZ', value: 'maz', sortable: false, width: '60' },
            { title: 'AR', value: 'ar', sortable: false, width: '55' },
            { title: 'Serratura', value: 'serratura', sortable: false, width: '90' },
            { title: 'Zanz', value: 'zanz', sortable: false, width: '70' },
            { title: 'Vetro', value: 'vetro', sortable: false, width: '70' },
            { title: '', value: 'actions', sortable: false, width: '100', align: 'end' }
        ]

        const rigaVuota = {
            id: null, rif: '', tipo: '', qta: '', l: '', h: '', nBat: '',
            spinta: '', maz: '', ar: '', serratura: '', zanz: '',
            vetro: '', antonePersiana: '', scuroInterno: '', batt: ''
        }

        const rigaCorrente = ref({ ...rigaVuota })

        const rules = {
            required: value => !!value || 'Campo obbligatorio'
        }

        watch(() => props.righe, (newVal) => {
            righeInterne.value = [...newVal]
        }, { deep: true })

        const nuovaRiga = () => {
            rigaCorrente.value = { ...rigaVuota }
            dialogModifica.value = true
        }

        const modificaRiga = (riga) => {
            rigaCorrente.value = { ...riga }
            dialogModifica.value = true
        }

        const duplicaRiga = (riga) => {
            rigaCorrente.value = { ...riga, id: null }
            dialogModifica.value = true
        }

        const salvaRiga = async () => {
            const { valid } = await formRigaRef.value.validate()
            if (!valid) return

            loading.value = true
            try {
                if (rigaCorrente.value.id) {
                    await modifica_riga_misura(props.ordineId, rigaCorrente.value.id, rigaCorrente.value)
                    const index = righeInterne.value.findIndex(r => r.id === rigaCorrente.value.id)
                    righeInterne.value[index] = { ...rigaCorrente.value }
                } else {
                    const response = await crea_riga_misura(props.ordineId, rigaCorrente.value)
                    righeInterne.value.push(response.data || response)
                }
                emit('righe-aggiornate', righeInterne.value)
                chiudiDialog()
            } catch (error) {
                console.error('Errore salvataggio riga:', error)
                alert('Errore durante il salvataggio')
            }
            loading.value = false
        }

        const eliminaRiga = async (riga) => {
            if (!confirm('Sei sicuro di voler eliminare questa riga?')) return

            loading.value = true
            try {
                await elimina_riga_misura(props.ordineId, riga.id)
                righeInterne.value = righeInterne.value.filter(r => r.id !== riga.id)
                emit('righe-aggiornate', righeInterne.value)
            } catch (error) {
                console.error('Errore eliminazione riga:', error)
                alert('Errore durante l\'eliminazione')
            }
            loading.value = false
        }

        const chiudiDialog = () => {
            dialogModifica.value = false
            rigaCorrente.value = { ...rigaVuota }
        }

        return {
            loading, dialogModifica, formRigaRef, righeInterne,
            headers, rigaCorrente, rules,
            nuovaRiga, modificaRiga, duplicaRiga,
            salvaRiga, eliminaRiga, chiudiDialog
        }
    }
}
</script>

<style scoped>
/* ── Card contenitore ── */
.comp-misure {
    margin: 0 auto;
}

/* ── Header ── */
.comp-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: #fafafa;
    border-radius: 8px 8px 0 0;
}

/* ── Stato vuoto ── */
.empty-state {
    text-align: center;
    padding: 28px 0 8px;
}

/* ── Tabella: scroll orizzontale su mobile ── */
.table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
}

.misure-table {
    min-width: 780px;
    /* forza scroll prima di schiacciare le colonne */
    font-size: 0.82rem;
}

/* Header celle: stile sezione-titolo come FormOrdine */
.misure-table :deep(thead tr th) {
    background: #f7f7f8 !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
    font-size: 0.75rem !important;
    font-weight: 600;
    color: #5a5a6e;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    height: 36px !important;
    white-space: nowrap;
}

/* Righe alternate leggere */
.misure-table :deep(tbody tr:nth-child(even)) {
    background: #fafafa;
}

.misure-table :deep(tbody tr:hover) {
    background: #eef0f3 !important;
}

/* Celle compatte */
.misure-table :deep(tbody td) {
    height: 36px !important;
    padding: 0 10px !important;
    white-space: nowrap;
}

/* Azioni raggruppate senza gap eccessivo */
.row-actions {
    display: flex;
    gap: 2px;
    justify-content: flex-end;
}

/* ── Dialog header ── */
.dialog-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: #f7f7f8;
    border-radius: 8px 8px 0 0;
}
</style>